import { useEffect, useState } from "preact/hooks"
import { normalizeShopifyVariant } from "../helpers/productVariants"

const shopifyVariantCache = new Map()
const SHOPIFY_STOREFRONT_ENDPOINT = "https://naadamcashmere.myshopify.com/api/2025-07/graphql.json"
const buildRequestBody = (productId) => ({
    query: `query ProductVariants($id: ID!) {
        product(id: $id) {
            productPromo: metafield(namespace: "product", key: "plp_promo_message") {
                value
            }
            images(first: 250) {
                edges {
                    node {
                        id
                        url
                        altText
                    }
                }
            }
            variants(first: 250) {
                edges {
                    node {
                        id
                        title
                        variantPromo: metafield(namespace: "promo_message", key: "plp_message") {
                            value
                        }
                        availableForSale
                        quantityAvailable
                        price {
                            amount
                            currencyCode
                        }
                        compareAtPrice {
                            amount
                            currencyCode
                        }
                        selectedOptions {
                            name
                            value
                        }
                    }
                }
            }
        }
    }`,
    variables: {
        id: `gid://shopify/Product/${productId}`
    }
})

export const useShopifyVariants = (product) => {
    const [shopifyData, setShopifyData] = useState({
        shopifySkus: [],
        shopifyImages: []
    })

    useEffect(() => {
        let cancelled = false

        const fetchShopifyVariants = async () => {
            if (!product?.productId) return

            const storefrontToken = typeof window !== "undefined"
                ? window.__GLOBAL__?.storefrontApiAccessToken
                : undefined

            if (!storefrontToken) return

            try {
                const cacheKey = String(product.productId)
                if (shopifyVariantCache.has(cacheKey)) {
                    if (!cancelled) {
                        setShopifyData(shopifyVariantCache.get(cacheKey))
                    }
                    return
                }

                const response = await fetch(SHOPIFY_STOREFRONT_ENDPOINT, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Accept": "application/json",
                        "X-Shopify-Storefront-Access-Token": storefrontToken
                    },
                    body: JSON.stringify(buildRequestBody(product.productId))
                })

                if (!response.ok) {
                    throw new Error(`Storefront API responded with ${response.status}`)
                }

                const payload = await response.json()

                if (payload.errors) {
                    throw new Error(payload.errors.map((err) => err.message).join(", "))
                }

                const productPromoText = payload?.data?.product?.productPromo?.value || ""

                const variantEdges = payload?.data?.product?.variants?.edges || []
                const imageEdges = payload?.data?.product?.images?.edges || []

                const normalizedSkus = variantEdges
                    .map(({ node }) => normalizeShopifyVariant(node, product.price, productPromoText))
                    .filter(Boolean)

                const galleryImages = imageEdges
                    .map(({ node }) => ({
                        id: node?.id,
                        url: node?.url,
                        altText: node?.altText
                    }))
                    .filter((image) => Boolean(image.url))

                if (!cancelled) {
                    const data = {
                        shopifySkus: normalizedSkus,
                        shopifyImages: galleryImages
                    }

                    shopifyVariantCache.set(cacheKey, data)
                    setShopifyData(data)
                }
            } catch (error) {
                console.error("Failed to fetch Shopify variants", error)
            }
        }

        fetchShopifyVariants()

        return () => {
            cancelled = true
        }
    }, [product?.productId, product?.price])

    return shopifyData
}
